/**
 * Authentication Service
 * Handles authentication operations and token management
 */
import { get, post } from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import { STORAGE_KEYS } from '../../constants/systemConfig';

/**
 * Login user
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.email - User email
 * @param {string} credentials.password - User password
 * @returns {Promise} Login response
 */
export const login = async (credentials) => {
  const response = await post(API_ENDPOINTS.AUTH.LOGIN, credentials);

  if (response.token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refreshToken);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.user));
  }

  return response;
};

/**
 * Register new user
 * @param {Object} userData - Registration data
 * @returns {Promise} Registration response
 */
export const register = async (userData) => {
  const response = await post(API_ENDPOINTS.AUTH.REGISTER, userData);
  return response;
};

/**
 * Logout user
 * @returns {Promise} Logout response
 */
export const logout = async () => {
  try {
    await post(API_ENDPOINTS.AUTH.LOGOUT);
  } finally {
    // Clear storage regardless of API response
    clearAuthData();
  }
};

/**
 * Clear authentication data from storage
 */
export const clearAuthData = () => {
  localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USER);
};

/**
 * Get current user data
 * @returns {Promise} User data
 */
export const getCurrentUser = async () => {
  const response = await get(API_ENDPOINTS.AUTH.ME);
  return response;
};

/**
 * Forgot password
 * @param {string} email - User email
 * @returns {Promise} Response
 */
export const forgotPassword = async (email) => {
  const response = await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email });
  return response;
};

/**
 * Reset password
 * @param {Object} data - Reset data
 * @returns {Promise} Response
 */
export const resetPassword = async (data) => {
  const response = await post(API_ENDPOINTS.AUTH.RESET_PASSWORD, data);
  return response;
};

/**
 * Change password
 * @param {Object} data - Password change data
 * @returns {Promise} Response
 */
export const changePassword = async (data) => {
  const response = await post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, data);
  return response;
};

/**
 * Update user profile
 * @param {Object} profileData - Profile update data
 * @returns {Promise} Response
 */
export const updateProfile = async (profileData) => {
  const response = await post(API_ENDPOINTS.AUTH.UPDATE_PROFILE, profileData);

  // Update stored user data
  const currentUser = getStoredUser();
  if (currentUser) {
    const updatedUser = { ...currentUser, ...response.user };
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(updatedUser));
  }

  return response;
};

/**
 * Verify email address
 * @param {string} token - Verification token
 * @returns {Promise} Response
 */
export const verifyEmail = async (token) => {
  const response = await get(`${API_ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${token}`);
  return response;
};

/**
 * Refresh access token
 * @returns {Promise} New token response
 */
export const refreshToken = async () => {
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await post(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
    refreshToken,
  });

  if (response.token) {
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.token);
  }

  return response;
};

/**
 * Get stored auth token
 * @returns {string|null} Auth token
 */
export const getToken = () => {
  return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Get stored refresh token
 * @returns {string|null} Refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
};

/**
 * Get stored user data
 * @returns {Object|null} User data
 */
export const getStoredUser = () => {
  const userStr = localStorage.getItem(STORAGE_KEYS.USER);
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if authenticated
 */
export const isAuthenticated = () => {
  return !!getToken();
};

/**
 * Update stored user data
 * @param {Object} userData - Updated user data
 */
export const updateStoredUser = (userData) => {
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
};

export default {
  login,
  register,
  logout,
  clearAuthData,
  getCurrentUser,
  forgotPassword,
  resetPassword,
  changePassword,
  updateProfile,
  verifyEmail,
  refreshToken,
  getToken,
  getRefreshToken,
  getStoredUser,
  isAuthenticated,
  updateStoredUser,
};
